import { useSiteSettings } from '@ottd/api';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import type { TFunction } from 'i18next';

import { SiteFooter } from '@/components/layout/site-footer';
import { TopNav } from '@/components/layout/top-nav';
import { useAnalytics } from '@/hooks/use-analytics';
import { useDocumentMeta } from '@/hooks/use-document-meta';

/**
 * Derives a per-route document title from the current path (SEO baseline -
 * see the SEO decision in docs/phase-7a.md). Dynamic segments read from the
 * URL so `/player/duchna.cz` and `/game/42` get meaningful titles too.
 */
function routeTitle(pathname: string, t: TFunction<'common'>): string | undefined {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const [first, second] = segments;

  switch (first) {
    case '':
      return undefined; // home → base title
    case 'server':
      return second ? t('meta.server', { id: decodeURIComponent(second) }) : t('meta.servers');
    case 'game':
      return second ? t('meta.game', { id: decodeURIComponent(second) }) : t('meta.games');
    case 'player':
      if (second === 'top') return t('meta.top100');
      return second ? decodeURIComponent(second) : t('meta.players');
    case 'season':
      return second ? t('meta.season', { num: decodeURIComponent(second) }) : t('meta.seasons');
    case 'ip':
    case 'asn':
      return t('meta.ipLookup');
    case 'legal':
      return t('meta.legal');
    case 'login':
      return t('meta.login');
    case 'register':
      return t('meta.register');
    case 'settings':
      return t('meta.settings');
    case 'token':
      return t('meta.loginToken');
    case 'email':
      return t('meta.verifyEmail');
    case 'forgot':
    case 'reset':
      return t('meta.resetPassword');
    case 'user':
      return undefined;
    default:
      return t('meta.notFound');
  }
}

/**
 * The public app shell: prototype top nav + routed content + footer. It also
 * applies the admin-managed site settings: document meta + og tags, then
 * analytics (after the meta, so each page_view carries the final title).
 */
export function PublicLayout() {
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  const { data: site } = useSiteSettings();

  useDocumentMeta({ title: routeTitle(pathname, t), site });
  useAnalytics(site?.analyticsId);

  return (
    <div className="flex min-h-screen flex-col">
      <TopNav />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}
